import type { AppearanceTheme } from '@molt/shared';

const STORAGE_KEY = 'molt.appearance';

/**
 * ⚠ `FE-04.18` — the `try/catch` around both storage calls below is for a
 * BROWSER that refuses storage (Safari's private mode threw on `setItem` for
 * years). It also absorbed a broken TEST HARNESS identically: until `FE-04.18`,
 * `window.localStorage` under Vitest was an object with no methods, so
 * `mirrorAppearancePreference` was a silent no-op and `readMirror` always
 * returned `null` — and this module had no test file at all, because one
 * written the obvious way would have thrown.
 *
 * ⚠ No `typeof storage?.getItem === 'function'` guard was added here, and that
 * is deliberate. `lib/view-state.ts` has one, with a module-scope `Map`
 * fallback behind it, and that pair is precisely what let its storage path be
 * skipped silently for three chunks. A suite that needs storage asks for it —
 * `installMemoryStorage` in `apps/web/test/setup.ts` — rather than the module
 * quietly succeeding without it. `theme.test.ts` beside this file is the
 * coverage that buys, against `FR-EXP-024` AC4 and `FR-EXP-021`.
 *
 * ── `FE-04.19` — the mirror is KEYED TO A MEMBER ────────────────────────────
 *
 * The record carries the id of the member whose preference it is, and
 * `reconcileMirroredAppearancePreference` throws it away the moment the
 * session resolves to anybody else — including to nobody.
 *
 * ⚠ **Read this before you conclude the first frame is now correct for an
 * arbitrary visitor. It is not, and it cannot be.** The mirror is read at
 * module scope in `main.tsx`, before React mounts and therefore before any
 * network request has been answered; the only identity the client can obtain
 * is `GET /me`, which has not returned, and Better Auth's session cookie is
 * not readable from script. **At the instant the first frame is painted the
 * client does not know who is looking at it**, and a stamp cannot change that
 * — a stamp can only be COMPARED, one round trip later. Delivering a correct
 * first frame to an unknown visitor needs the server to write `data-theme`
 * onto the document, which needs SSR, which `apps/web` does not have
 * (`BE-04.1` records that).
 *
 * So what the stamp actually buys is stated precisely, because the difference
 * is the whole point of the item: **the mirror stops being a private setting
 * that leaks PERMANENTLY to whoever next uses the browser, and becomes one
 * that is corrected at the first moment an identity is knowable.** The
 * unbounded case is the one that mattered and it was not the one the residual
 * named — see `reconcileMirroredAppearancePreference`.
 */

interface MirroredAppearancePreference {
  readonly theme: AppearanceTheme;
  readonly colourBlindMode: boolean;
  /**
   * `null` is the LEGACY record — one written by the pre-`FE-04.19` code,
   * which stored no identity at all. It is not an error and it is not
   * discarded on sight; `reconcileMirroredAppearancePreference` explains what
   * happens to it and why.
   */
  readonly userId: string | null;
}

/**
 * The three values are not a client-side invention and must not be loosened:
 * they are `ck_user_preferences_theme`, the CHECK constraint on the column
 * this mirrors, spelled identically.
 */
function isTheme(value: unknown): value is AppearanceTheme {
  return value === 'light' || value === 'dark' || value === 'match_system';
}

function readMirror(): MirroredAppearancePreference | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as {
      readonly theme?: unknown;
      readonly colourBlindMode?: unknown;
      readonly userId?: unknown;
    };
    if (!isTheme(parsed.theme) || typeof parsed.colourBlindMode !== 'boolean') {
      return null;
    }
    /*
     * ⚠ A missing `userId` is the legacy shape and is NOT a reason to discard
     * the record — unlike a missing `colourBlindMode`, which is. The
     * difference is that a half-written preference would paint two disagreeing
     * halves on the first frame, whereas an unstamped one paints a coherent
     * preference whose owner is merely unknown. An empty string is treated as
     * absent rather than as an id, so a truncated write cannot masquerade as a
     * member nobody can be.
     */
    const userId =
      typeof parsed.userId === 'string' && parsed.userId.length > 0
        ? parsed.userId
        : null;
    return { theme: parsed.theme, colourBlindMode: parsed.colourBlindMode, userId };
  } catch {
    return null;
  }
}

export function applyThemeAttributes(
  element: HTMLElement,
  theme: AppearanceTheme,
  systemDark: boolean,
): void {
  const resolved = theme === 'match_system' ? (systemDark ? 'dark' : 'light') : theme;
  element.dataset['theme'] = resolved;
  element.dataset['appearanceTheme'] = theme;
  element.style.colorScheme = resolved;
}

/**
 * The inverse of `applyThemeAttributes` plus the colour-blind flag — every
 * attribute this module is capable of writing, removed together.
 *
 * ⚠ It clears `colourBlindLabels` as well, which `applyThemeAttributes` does
 * not set. That asymmetry is deliberate: the two attributes are written by one
 * effect from one stored record, so leaving the accessibility flag behind
 * while removing the theme would leave the document in a state no member ever
 * chose — the previous member's colour-blind setting over the next member's
 * theme.
 */
function clearThemeAttributes(element: HTMLElement): void {
  delete element.dataset['theme'];
  delete element.dataset['appearanceTheme'];
  delete element.dataset['colourBlindLabels'];
  element.style.colorScheme = '';
}

/**
 * Remove the record and undress the document, in that order and always
 * together. The `try/catch` matches the write path's contract exactly: a
 * browser that refuses storage still gets a CORRECT DOCUMENT, because the
 * attributes come off whether or not the key could be removed.
 */
function discardMirror(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage is an optimisation for reload paint only; the server remains the
    // source, and the document below is the half that must not be left wrong.
  }
  clearThemeAttributes(document.documentElement);
}

/**
 * Write-through, stamped with the member the preference belongs to.
 *
 * `userId` is REQUIRED rather than optional, and that is the compile-time half
 * of this item: the only caller is `__root.tsx`'s appearance effect, which
 * runs solely for a resolved signed-in session, so there is no legitimate way
 * to write an unstamped record any more. Making it optional would have left
 * the defect reachable by omission.
 */
export function mirrorAppearancePreference(
  theme: AppearanceTheme,
  colourBlindMode: boolean,
  userId: string,
): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ theme, colourBlindMode, userId }),
    );
  } catch {
    // Storage is an optimisation for reload paint only; the server remains the source.
  }
}

export function applyMirroredAppearancePreference(): void {
  const mirrored = readMirror();
  if (mirrored === null) return;
  const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  applyThemeAttributes(document.documentElement, mirrored.theme, systemDark);
  document.documentElement.dataset['colourBlindLabels'] = String(
    mirrored.colourBlindMode,
  );
}

/** What `reconcileMirroredAppearancePreference` did, for the caller's tests. */
export type MirrorReconciliation = 'absent' | 'kept' | 'discarded';

/**
 * ── `FE-04.19` — the whole behavioural contract of the stamp ────────────────
 *
 * Called once, from `__root.tsx`, **when the session resolves** — never when
 * the appearance query resolves. That relocation IS the fix, and the reason is
 * the defect the residual missed:
 *
 * `appearanceQuery` is gated `enabled: !isSignedOut && !isPending && profile
 * !== null`, and the effect that paints the document hangs off its `data`. So
 * when a member signs out, `data` becomes `undefined`, the query is disabled
 * and never runs again, and the painting effect returns early — **nothing in
 * the tree ever writes those three attributes again.** The signed-out surface
 * therefore keeps wearing the departed member's private theme, and
 * `molt.appearance` keeps holding their preference, across every subsequent
 * reload, for every later visitor to that browser, with no event that ever
 * corrects it. `FR-EXP-024`'s own edge case says *"the theme is user-level and
 * private"*; that is a private setting displayed to an unauthenticated
 * visitor indefinitely. Hanging the reconciliation off the SESSION instead is
 * what bounds it, because `GET /me` answers for a signed-out visitor and the
 * appearance query does not.
 *
 * `sessionUserId` is the resolved identity: a ULID, or `null` for a session
 * that resolved to nobody. **Do not call it while the session is pending** —
 * `isPending` means we do not know yet, and treating "no profile yet" as
 * "signed out" is the exact bug `lib/session.ts` warns about, which would here
 * discard a valid mirror on every single load and cost AC4 outright.
 *
 * | mirror | session | action |
 * |---|---|---|
 * | absent | signed-in | nothing |
 * | absent | signed out | **strip the document** — see the note below |
 * | stamped `A` | signed-in `A` | **keep** — the returning member; AC4's common case |
 * | stamped `A` | signed-in `B` | **discard** — B's own preference re-stamps a moment later |
 * | stamped `A` | signed out | **discard** — nobody owns this view |
 * | unstamped | signed-in `X` | **keep**, and the appearance effect re-stamps it as `X` |
 * | unstamped | signed out | **discard** |
 *
 * ⚠ **The strip is an invariant of the SESSION, not a property of the record,
 * and the audit's table had it as "absent → nothing" on the reasoning that
 * there is nothing to correct. That reasoning is wrong, and the counter-example
 * is not exotic: THE DOCUMENT CAN CARRY A PAINTED THEME WITH NO READABLE
 * MIRROR BEHIND IT.** `readMirror` folds two different situations to `null` —
 * a browser that refuses storage (the case the `try/catch` above exists for,
 * which is private mode, i.e. exactly the browsers most likely to be shared)
 * and a record this module cannot parse. In both, the appearance effect in
 * `__root.tsx` still painted `data-theme` from the *server's* answer while the
 * member was signed in, and the mirror had nothing to do with it. Strip only
 * the record and that paint survives the sign-out for the life of the SPA
 * session — §1.3's leak again, one storey down, in the browsers that can least
 * afford it. So when the session has resolved to nobody the three attributes
 * come off unconditionally.
 *
 * The return value stays `'absent'` in that case, deliberately: it reports what
 * was found in STORAGE, and nothing was. An unreadable record is also left
 * where it is rather than removed — a record this module cannot read is not one
 * it owns, it paints nothing through `applyMirroredAppearancePreference`, and
 * removing it would be a claim on somebody else's key.
 *
 * ⚠ **The two unstamped rows are the migration decision, and they disagree on
 * purpose.** Every member who has loaded this product since `C1` has an
 * unstamped record sitting in their browser, and an unstamped record written
 * by the shipped code is indistinguishable from one written by a different
 * member. Discarding them all on sight would charge **every existing member
 * one flash of the wrong theme on their next reload** — a guaranteed,
 * universal regression of the very acceptance criterion this module exists for
 * — to defend against a case that is rare and that the next line of the same
 * effect repairs anyway, since `mirrorAppearancePreference` re-stamps the
 * record as soon as the member's preferences arrive. So a signed-in member
 * honours it once. A signed-out session has no returning member to protect and
 * no theme is owned, so there the argument reverses and the record goes. The
 * legacy shape decays to nothing after each member's first signed-in load.
 */
export function reconcileMirroredAppearancePreference(
  sessionUserId: string | null,
): MirrorReconciliation {
  const mirrored = readMirror();

  /*
   * Nobody owns this view. The document is stripped whether or not there is a
   * readable record behind it — see the note above; that is the invariant, and
   * the record is only the commonest reason the document is dressed.
   */
  if (sessionUserId === null) {
    if (mirrored === null) {
      clearThemeAttributes(document.documentElement);
      return 'absent';
    }
    discardMirror();
    return 'discarded';
  }

  if (mirrored === null) return 'absent';
  if (mirrored.userId === null || mirrored.userId === sessionUserId) return 'kept';

  discardMirror();
  return 'discarded';
}
